import { useEffect, useState } from "react";
import { api } from "@/lib/api";

interface Notification {
  id: string;
  title?: string;
  message?: string;
  read: boolean;
  createdAt: string;
}

export function InboxPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await api.getNotifications();
      setNotifications(data || []);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markRead = async (id: string) => {
    await api.markNotificationRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllRead = async () => {
    await api.markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-zinc-100">Inbox</h1>
          {unread > 0 && (
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
              {unread}
            </span>
          )}
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            className="rounded-lg bg-zinc-800 px-3 py-1.5 text-xs text-zinc-400 hover:bg-zinc-700"
          >
            Mark all read
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-zinc-500">
            Loading...
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-zinc-500">
            <div className="text-4xl">📬</div>
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.read && markRead(n.id)}
                className={`flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left transition-colors ${
                  n.read
                    ? "bg-transparent hover:bg-zinc-900/50"
                    : "bg-blue-600/5 hover:bg-blue-600/10"
                }`}
              >
                {!n.read && (
                  <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                )}
                <div className="min-w-0 flex-1">
                  {n.title && (
                    <div className="text-sm font-medium text-zinc-200">{n.title}</div>
                  )}
                  {n.message && (
                    <div className="mt-0.5 text-xs text-zinc-500 line-clamp-2">
                      {n.message}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-[10px] text-zinc-600">
                  {new Date(n.createdAt).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
